import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  GetApp as ExportIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  PersonOff as PersonOffIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import {
  getAttendanceStats,
  selectAttendanceStats,
  selectAttendanceLoading,
  selectAttendanceError,
  clearError,
} from "../../../store/slices/attendanceSlice";
import { useAuth } from "../auth/AuthGuard";
import { ROLES } from "../../../constants";
import attendanceAPI from "../../../services/api/attendance.api";
import { fetchDepartments } from "../../../store/slices/departmentSlice";
import { selectDepartments } from "../../../store/slices/departmentSlice";

const AttendanceReports = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();

  const attendanceStats = useSelector(selectAttendanceStats);
  const loading = useSelector(selectAttendanceLoading);
  const error = useSelector(selectAttendanceError);
  const departments = useSelector(selectDepartments) || [];

  // Applied filters (trigger fetch)
  const [appliedFilters, setAppliedFilters] = useState({
    period: "monthly",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    department: "",
  });
  // Pending filters (UI controls before Apply)
  const [pendingFilters, setPendingFilters] = useState({ ...appliedFilters });

  const [departmentBreakdown, setDepartmentBreakdown] = useState([]);
  const [todayAbsentEmployees, setTodayAbsentEmployees] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const isManager = hasAnyRole([
    ROLES.ADMIN,
    ROLES.HR_MANAGER,
    ROLES.DEPARTMENT_HEAD,
  ]);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...pendingFilters });
  };

  const loadReportsData = useCallback(() => {
    dispatch(getAttendanceStats({
      period: appliedFilters.period,
      month: appliedFilters.month,
      year: appliedFilters.year,
      department_id: appliedFilters.department || undefined,
    }));
  }, [dispatch, appliedFilters]);

  const loadOverviewData = useCallback(async (filters) => {
    setOverviewLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();

      const overviewParams = {};
      if (filters?.department) overviewParams.department_id = filters.department;

      const [overviewRes, absentRes] = await Promise.all([
        attendanceAPI.getAttendanceOverview(overviewParams),
        attendanceAPI.getEmployeeAttendance('all', {
          status: 'absent',
          month: currentMonth,
          year: currentYear,
          limit: 100,
        }),
      ]);

      const overviewData = overviewRes?.data || overviewRes || {};
      setDepartmentBreakdown(overviewData.department_breakdown || []);

      const absentData = absentRes?.data || absentRes || [];
      const rows = Array.isArray(absentData) ? absentData : (absentData.rows || []);
      setTodayAbsentEmployees(
        rows.filter(r => r.date === today).slice(0, 8)
      );
    } catch (err) {
      console.error('Error loading overview data:', err);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isManager) {
      dispatch(fetchDepartments({ limit: 100 }));
      loadOverviewData(appliedFilters);
    }
  }, [isManager, dispatch, appliedFilters, loadOverviewData]);

  useEffect(() => {
    if (isManager) {
      loadReportsData();
    }
  }, [isManager, loadReportsData]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
      dispatch(clearError());
    }
  }, [error, enqueueSnackbar, dispatch]);

  const handleExportReport = async () => {
    try {
      const params = {
        period: appliedFilters.period,
        month: appliedFilters.month,
        year: appliedFilters.year,
        department_id: appliedFilters.department || undefined,
        format: "excel",
      };

      const blob = await attendanceAPI.exportAttendanceReport(params);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `attendance_report_${appliedFilters.year}_${appliedFilters.month}.xlsx`;
      link.click();

      window.URL.revokeObjectURL(url);
      enqueueSnackbar("Report exported successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to export report", { variant: "error" });
    }
  };

  // Use actual data from API
  const stats = attendanceStats || {
    totalEmployees: 0,
    presentToday: 0,
    absentToday: 0,
    onLeaveToday: 0,
    attendanceRate: 0,
    avgWorkingHours: 0,
    lateArrivals: 0,
    earlyDepartures: 0,
    monthlyTrends: [],
  };

  // Transform monthly trends from weeks to display format
  const monthlyTrends = (stats.monthlyTrends && Array.isArray(stats.monthlyTrends) && stats.monthlyTrends.length > 0)
    ? stats.monthlyTrends.map((trend, index) => ({
        month: trend.week || `Week ${index + 1}`,
        present: trend.present || 0,
        absent: trend.absent || 0,
        onLeave: trend.onLeave || 0,
      }))
    : [];

  // Calculate attendance breakdown from actual stats
  const attendanceBreakdown = [
    { name: "Present", value: stats.presentToday || 0, color: "#4caf50" },
    { name: "Absent", value: stats.absentToday || 0, color: "#f44336" },
    { name: "On Leave", value: stats.onLeaveToday || 0, color: "#2196f3" },
  ];
  const pieHasData = attendanceBreakdown.some(d => d.value > 0);

  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const periodLabel = {
    daily: "Daily",
    weekly: "Weekly",
    monthly: `${monthOptions.find(m => m.value === appliedFilters.month)?.label || ""} ${appliedFilters.year}`,
    yearly: `${appliedFilters.year}`,
  }[appliedFilters.period] || "Selected Period";

  if (!isManager) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Access restricted to managers and administrators only
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
            Attendance Reports
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive attendance analytics and insights
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<ExportIcon />}
          onClick={handleExportReport}
        >
          Export Report
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Period"
                value={pendingFilters.period}
                onChange={(e) => setPendingFilters(f => ({ ...f, period: e.target.value }))}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Month"
                value={pendingFilters.month}
                onChange={(e) => setPendingFilters(f => ({ ...f, month: e.target.value }))}
              >
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={pendingFilters.year}
                onChange={(e) => setPendingFilters(f => ({ ...f, year: parseInt(e.target.value) }))}
                inputProps={{ min: 2020, max: new Date().getFullYear() + 1 }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Department"
                value={pendingFilters.department}
                onChange={(e) => setPendingFilters(f => ({ ...f, department: e.target.value }))}
              >
                <MenuItem value="">All Departments</MenuItem>
                {(departments || []).map((dept) => (
                  <MenuItem key={dept.id} value={dept.id.toString()}>
                    {dept.department_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={handleApplyFilters}
                sx={{ height: 56 }}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "primary.main" }}
                  >
                    {stats.presentToday}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Present Today
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: "primary.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "success.main" }}
                  >
                    {stats.attendanceRate?.toFixed(1) || 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Attendance Rate
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: "success.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "info.main" }}
                  >
                    {stats.avgWorkingHours || 0}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Working Hours
                  </Typography>
                </Box>
                <ScheduleIcon sx={{ fontSize: 40, color: "info.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "warning.main" }}
                  >
                    {stats.lateArrivals || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Late Arrivals
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, color: "warning.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Attendance Trends Chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                Attendance Trends — {periodLabel}
              </Typography>

              {loading ? (
                <LinearProgress />
              ) : monthlyTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="present" fill="#4caf50" name="Present" />
                    <Bar dataKey="absent" fill="#f44336" name="Absent" />
                    <Bar dataKey="onLeave" fill="#2196f3" name="On Leave" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                  No data available for the selected period
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Attendance Breakdown Pie Chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                Today's Snapshot
              </Typography>

              {pieHasData ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={attendanceBreakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {attendanceBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
                  <Typography variant="body2" color="text.secondary">
                    No attendance recorded today
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Department Performance */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                Department Performance — Today
                {appliedFilters.department && departments.find(d => d.id.toString() === appliedFilters.department)
                  ? ` (${departments.find(d => d.id.toString() === appliedFilters.department).department_name})`
                  : ""}
              </Typography>

              {overviewLoading ? (
                <LinearProgress />
              ) : (departmentBreakdown && departmentBreakdown.length > 0) ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Department</TableCell>
                        <TableCell align="center">Employees</TableCell>
                        <TableCell align="center">Checked In</TableCell>
                        <TableCell align="center">Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {departmentBreakdown.map((dept) => {
                        const rate = parseFloat(dept.attendance_rate) || 0;
                        return (
                          <TableRow key={dept.department_id}>
                            <TableCell>{dept.department_name}</TableCell>
                            <TableCell align="center">{dept.total_employees}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={dept.present_today}
                                color="success"
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={rate}
                                  sx={{ width: 60, height: 6 }}
                                  color={rate >= 90 ? "success" : rate >= 80 ? "warning" : "error"}
                                />
                                <Typography variant="body2">{rate.toFixed(1)}%</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                  No department data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Absent Today */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <PersonOffIcon color="error" />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Absent Today
                </Typography>
                {todayAbsentEmployees.length > 0 && (
                  <Chip label={todayAbsentEmployees.length} color="error" size="small" />
                )}
              </Box>

              {overviewLoading ? (
                <LinearProgress />
              ) : todayAbsentEmployees.length > 0 ? (
                <List dense disablePadding>
                  {todayAbsentEmployees.map((record) => {
                    const user = record.user || {};
                    const fullName = [user.first_name, user.middle_name, user.sur_name]
                      .filter(Boolean).join(" ");
                    const dept = user.basicEmployeeData?.department?.department_name || "";
                    const initials = [user.first_name, user.sur_name]
                      .filter(Boolean).map(n => n[0]).join("").toUpperCase();
                    return (
                      <ListItem key={record.id} sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32, fontSize: 12, bgcolor: "error.light" }}>
                            {initials}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={fullName || user.email || "—"}
                          secondary={dept}
                          primaryTypographyProps={{ variant: "body2", fontWeight: "medium" }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <PersonOffIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No absences recorded today
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AttendanceReports;
